import { IhmSpyglassServiceClient } from '../services';
import { ServiceException } from '../types/CoralExceptionTypes';
import { Exception } from '../types/Exception';
import { LoadingCache } from '../utils/Cache';
import _ from 'lodash';
import { ProductInfo } from './ProductInfo';
import { AiCode } from './AiCode';
import { GS1CodeParser } from './GS1CodeParser';

export const ItemIdType = Object.freeze({
    ASIN: 'ASIN',
    EAN: 'EAN',
    FCSKU: 'FCSKU',
    FNSKU: 'FNSKU',
    PLU: 'PLU',
    UPC: 'UPC',
    KITCHEN_ITEM_ID: 'KITCHEN_ITEM_ID',
    GS1: 'GS1',
    UNKNOWN: 'UNKNOWN',
});

export class ItemIdWithType {
    public itemId: string;
    public idType: string;

    constructor(itemId: string, idType: string) {
        this.itemId = itemId;
        this.idType = idType;
    }
}

export class Asin extends ItemIdWithType {
    constructor(asin: string) {
        super(asin, ItemIdType.ASIN);
    }
}

export class FcSku extends ItemIdWithType {
    constructor(asin: string) {
        super(asin, ItemIdType.FCSKU);
    }
}

export class KitchenItemId extends ItemIdWithType {
    constructor(asin: string) {
        super(asin, ItemIdType.KITCHEN_ITEM_ID);
    }
}

export class FnSku extends ItemIdWithType {
    constructor(asin: string) {
        super(asin, ItemIdType.FNSKU);
    }
}

export class InvalidAsinFcSkuException extends Exception {}

const fcSkuOrAsinPattern = /^[A-Z0-9]{10}$/;
// FnSku (X00) label: 'X' prefix followed by 9 alphanumeric chars (10 total). MENA/India FBA
// inventory arrives with these X00 labels.
const fnSkuPattern = /^[Xx][0-9A-Za-z]{9}$/;
const upcPattern = /^([0-9]{8}|[0-9]{11}|[0-9]{12}|0[0-9]{12})$/;
const eanPattern = /^[0-9]{13}$/;
const pluPattern = /^([0-9]{4,5})$/;

// GS1-128 barcodes can vary widely in length and format but at minimum we are expecting them to have GTIN.
// This pattern detection is flexible and might need revisiting if other long barcodes need to be supported,
// but this should be safe for now since it's larger than all others.
// At minimum the code should have a length of 16 for the GTIN: 14 (GTIN) + 2 (AI Code for GTIN)
const gs1Pattern = /^[a-zA-Z0-9]{16,}$/;

interface IItemIdWithTypeKey {
    itemId: string;
    storeId: string;
}

export class ItemIdWithTypeUtils {
    /**
     * This function is used to try to detect an Id Type for the provided
     * itemId. It will return a value of the form { itemId:'value', idType:'value' }.
     * If it fails to find a valid idType then it will return with the idType 'UNKNOWN'.
     * Supported idTypes are: ASIN, RFID, UPC, EAN, PLU, and GS1-128.
     *
     * @param itemId An Id that you would like to try to convert to an itemIdWithType
     * @param storeId
     * @return Returns a promise with an itemIdWithType value in the form: { itemId:'value', idType:'value' }
     */
    public static convertToItemIdWithType = (itemId: string, storeId: string) => {
        let promise;
        if (fcSkuOrAsinPattern.test(itemId)) {
            promise = ItemIdWithTypeUtils.asinFcSkuTypeCache.get({ storeId, itemId });
        } else if (upcPattern.test(itemId)) {
            try {
                const upcA = UpcUtils.convertToUpcA(itemId);
                promise = Promise.resolve(new ItemIdWithType(upcA, ItemIdType.UPC));
            } catch (err) {
                promise = Promise.resolve(new ItemIdWithType(itemId, ItemIdType.UNKNOWN));
            }
        } else if (eanPattern.test(itemId)) {
            promise = Promise.resolve(new ItemIdWithType(itemId, ItemIdType.EAN));
        } else if (pluPattern.test(itemId)) {
            promise = Promise.resolve(new ItemIdWithType(itemId, ItemIdType.PLU));
        } else if (gs1Pattern.test(itemId)) {
            promise = Promise.resolve(new ItemIdWithType(itemId, ItemIdType.GS1));
        } else {
            promise = Promise.resolve(new ItemIdWithType(itemId, ItemIdType.UNKNOWN));
        }
        return promise;
    };

    public static isItemIdWithType = (itemIdWithType: ItemIdWithType) => {
        return _.isObject(itemIdWithType) && _.isString(itemIdWithType.idType) && _.isString(itemIdWithType.itemId) && !_.isUndefined(ItemIdType[itemIdWithType.idType]);
    };

    /**
     *  This function handles looking up the ASIN for the provided
     */
    public static getAsinsOrFcSkuForId = (storeId: string, itemId: string) => {
        return ItemIdWithTypeUtils.asinsOrFcSkuForIdCache.get({ storeId, itemId });
    };

    /**
     * Attempts to get the FnSku for an ID, but if there is more than one possible FnSku a list of
     * all ASINs are returned instead. All IDs are returned in ItemIdWithType format
     */
    public static getFnSkuOrAsinsForId = (storeId: string, itemId: string) => {
        return ItemIdWithTypeUtils.getFnSkuOrAsinsForIdCache.get({ storeId, itemId });
    };

    /**
     * Resolves a scanned id to its ASIN(s). If the scan is an FnSku (X00) label, the FnSku is
     * converted to its ASIN via SpyGlass GetSkusForItemId so the tool and ALL downstream API calls
     * operate on ASIN, matching the default (non-SAPS) behaviour. Non-FnSku scans
     * (UPC/EAN/ASIN/FcSku) resolve exactly as getAsinsOrFcSkuForId. Used by MENA QC dark stores
     * where FBA inventory arrives with X00 labels but the tool must keep operating on ASIN.
     *
     * @param storeId
     * @param itemId An Id that may be an FnSku (X00) label or any other supported id
     * @return A promise resolving to a list of ItemIdWithType, always ASIN idType for an FnSku input
     */
    public static getAsinForFnSkuOrId = (storeId: string, itemId: string) => {
        // Barcode preprocessing to detect the underlying id before matching the FnSku pattern.
        let processedId = BarcodeUtils.extractPLUFromType2Barcode(itemId);
        if (gs1Pattern.test(processedId)) {
            processedId = BarcodeUtils.extractGtinFromGs1Barcode(itemId);
        }
        if (fnSkuPattern.test(processedId)) {
            return ItemIdWithTypeUtils.asinForFnSkuCache.get({ storeId, itemId: processedId });
        }
        // Not an FnSku (regular ASIN/FcSku/UPC/EAN): resolve exactly as the default path.
        return ItemIdWithTypeUtils.getAsinsOrFcSkuForId(storeId, itemId);
    };

    private static _asinFcSkuTypeProvider = (key: IItemIdWithTypeKey) => {
        let asinError;

        const asinIdType = new ItemIdWithType(key.itemId, ItemIdType.ASIN);
        return ProductInfo.getProductInfo(key.storeId, asinIdType)
            .then((productInfo: object) => {
                if ('DESCRIPTION' in productInfo) {
                    return asinIdType;
                } else {
                    return Promise.reject(new Exception({ message: 'No description; not a valid ASIN' }));
                }
            })
            .catch((error) => {
                asinError = error;
                const fcSkuIdType = new ItemIdWithType(key.itemId, ItemIdType.FCSKU);
                return ProductInfo.getProductInfo(key.storeId, fcSkuIdType).then((productInfo: object) => {
                    if ('DESCRIPTION' in productInfo) {
                        return fcSkuIdType;
                    } else {
                        return Promise.reject(new Exception({ message: 'No description; not a valid FcSku' }));
                    }
                });
            })
            .catch((error) => {
                // TODO: investigate how to appropriately get the error message when the exception is not an object
                // CrossFrame stringfy the errors. See:
                // https://tiny.amazon.com/18uwhrr99/codeamazpackIhmAblobbf1asrc
                const asinErrorMesssage = asinError && typeof asinError.getMessage === 'function' ? asinError.getMessage() : asinError;

                const errorMesssage = error && typeof error.getMessage === 'function' ? error.getMessage() : error;

                return Promise.reject(
                    new InvalidAsinFcSkuException({
                        message: 'Could not convert ' + key.itemId + ' to ASIN or FcSku. ' + 'Reasons: (1) ' + asinErrorMesssage + ' (2) ' + errorMesssage,
                    }),
                );
            });
    };

    private static _asinFcSkuTypeKeyHasher = (key: IItemIdWithTypeKey) => {
        return key.itemId;
    };

    private static asinFcSkuTypeCache = new LoadingCache({
        maxSize: 1000,
        // @ts-ignore
        provider: ItemIdWithTypeUtils._asinFcSkuTypeProvider,
        // @ts-ignore
        keyHasher: ItemIdWithTypeUtils._asinFcSkuTypeKeyHasher,
        shouldCacheFailures: true,
        ttlForFailures: 30,
    });

    private static _asinsOrFcSkuForIdProvider = async (key: IItemIdWithTypeKey) => {
        let itemId = key.itemId;
        if (upcPattern.test(itemId) && itemId.length === 11) {
            // If the itemId is a UPC code-type of length 11, normalize it.
            try {
                itemId = UpcUtils.convertToUpcA(itemId);
            } catch (err) {
                // Go ahead and try to process the itemId anyway,
                // which will return an unknown product code error
                // if it cannot complete.
                console.debug('Error converting to UpcA, continuing anyways.');
            }
        }

        // For type 2 barcodes, extract the PLU within and use PLU for searching instead.
        itemId = BarcodeUtils.extractPLUFromType2Barcode(key.itemId);

        // If barcode is GS1 128, parse it and extract the GTIN if it's present
        if (gs1Pattern.test(itemId)) {
            itemId = BarcodeUtils.extractGtinFromGs1Barcode(key.itemId);
        }

        try {
            const response: any = await IhmSpyglassServiceClient.getAsinsForExternalItemId(key.storeId, itemId, [], true);
            let asins: string[] = response.asins.concat(response.secondaryIndexAsins || []);
            // Remove duplicates from the asins list
            asins = [...new Set(asins)];
            if (asins.length !== 0) {
                const asinsWithType = [];
                _.each(asins, function (asin) {
                    asinsWithType.push(new Asin(asin));
                });
                return asinsWithType;
            }
            // The last call should have returned a result if it was an ASIN, so it's possible it's an FcSku
            if (fcSkuOrAsinPattern.test(key.itemId)) {
                const fcSkuWithType = new FcSku(key.itemId);
                await ProductInfo.getProductInfo(key.storeId, fcSkuWithType);
                return [fcSkuWithType];
            }
        } catch (exception) {
            if (exception instanceof Exception) {
                // Not a valid FcSku
                return Promise.resolve([]);
            } else {
                return Promise.reject(exception);
            }
        }
        return [];
    };

    private static _asinsOrFcSkuForIdKeyHasher = (key: IItemIdWithTypeKey) => {
        return key.storeId + key.itemId;
    };

    private static asinsOrFcSkuForIdCache = new LoadingCache({
        maxSize: 1000,
        provider: ItemIdWithTypeUtils._asinsOrFcSkuForIdProvider,
        keyHasher: ItemIdWithTypeUtils._asinsOrFcSkuForIdKeyHasher,
    });

    private static _fnSkuOrAsinsForIdProvider = (key: IItemIdWithTypeKey) => {
        let itemId = key.itemId;

        // For type 2 barcodes, extract the PLU within and use PLU for searching instead.
        itemId = BarcodeUtils.extractPLUFromType2Barcode(key.itemId);

        // If barcode is GS1 128, parse it and extract the GTIN if it's present
        if (gs1Pattern.test(itemId)) {
            itemId = BarcodeUtils.extractGtinFromGs1Barcode(key.itemId);
        }

        return IhmSpyglassServiceClient.getSkusForItemId(key.storeId, itemId, false, [], [ItemIdType.FNSKU, ItemIdType.ASIN]).then((response) => {
            const asins = response.itemIdentifiers;
            if (asins && asins.length !== 0) {
                // If there's only one ASIN and only one FnSku for that ASIN, return it.
                // Otherwise, return the list of all ASINs
                if (asins.length === 1) {
                    const fnSkus = asins[0].childMappings;
                    if (fnSkus && fnSkus.length === 1) {
                        return [new FnSku(fnSkus[0].id)];
                    }
                }

                return _.map(asins, (asin) => new Asin(asin.id));
            }

            return [];
        });
    };

    private static _fnSkuOrAsinsForIdKeyHasher = (key: IItemIdWithTypeKey) => {
        return key.storeId + key.itemId;
    };

    private static getFnSkuOrAsinsForIdCache = new LoadingCache({
        maxSize: 1000,
        provider: ItemIdWithTypeUtils._fnSkuOrAsinsForIdProvider,
        keyHasher: ItemIdWithTypeUtils._fnSkuOrAsinsForIdKeyHasher,
    });

    /**
     * Converts an FnSku (X00) label to its ASIN(s) via SpyGlass GetSkusForItemId
     * (idTypes=[FNSKU], requestedTypes=[ASIN]). Always returns ASIN-typed ItemIdWithType so that
     * downstream calls behave as they do for a regular ASIN scan. Rejects if the FnSku cannot be
     * resolved (e.g. not present in the label registry).
     */
    private static _asinForFnSkuProvider = (key: IItemIdWithTypeKey) => {
        return IhmSpyglassServiceClient.getSkusForItemId(key.storeId, key.itemId, false, [ItemIdType.FNSKU], [ItemIdType.ASIN]).then((response) => {
            const asins = response.itemIdentifiers;
            if (asins && asins.length !== 0) {
                return _.map(asins, (asin) => new Asin(asin.id));
            }
            return Promise.reject(new InvalidAsinFcSkuException({ message: 'Could not resolve FnSku ' + key.itemId + ' to an ASIN' }));
        });
    };

    private static _asinForFnSkuKeyHasher = (key: IItemIdWithTypeKey) => {
        return key.storeId + key.itemId;
    };

    private static asinForFnSkuCache = new LoadingCache({
        maxSize: 1000,
        provider: ItemIdWithTypeUtils._asinForFnSkuProvider,
        keyHasher: ItemIdWithTypeUtils._asinForFnSkuKeyHasher,
    });
}

export class UpcUtils {
    /**
     * This function is used to convert the value from one barcode format
     * into the UPC-A format. The input formats that are currently supported
     * are: UPC-A (12 digits), UPC-A (11 digits missing check digit), UPC-E
     * (8 digits), and EAN-13 (13 digits) with * a leading 0.
     *
     * @param value A barcode value in one of the supported formats
     * @return Returns the 12 digit UPC-A formatted code
     */
    public static convertToUpcA = (value: string) => {
        const pattern = /^\d*$/;

        if (0 === value.length) {
            throw new Error('Input cannot be empty');
        } else if (!pattern.test(value)) {
            throw new Error('Input should contain only digits');
        } else if (value.length === 12) {
            // already a UPC-A code
            return value;
        } else if (value.length === 11) {
            return value + UpcUtils.calculateCheckDigit(value).toString();
        } else if (value.length === 13) {
            // an EAN-13 code
            if (value.charAt(0) !== '0') {
                throw new Error('Unsupported EAN-13 value provided. Only EAN-13 values starting with a 0 can be converted to UPC-A');
            }

            return value.substring(1);
        } else if (value.length === 8) {
            // a UPC-E code
            switch (value.charAt(6)) {
                case '0':
                case '1':
                case '2':
                    return value.substring(0, 3) + value.charAt(6) + '0000' + value.substring(3, 6) + value.charAt(7);
                case '3':
                    return value.substring(0, 4) + '00000' + value.substring(4, 6) + value.charAt(7);
                case '4':
                    return value.substring(0, 5) + '00000' + value.substring(5, 6) + value.charAt(7);
                case '5':
                case '6':
                case '7':
                case '8':
                case '9':
                    return value.substring(0, 6) + '0000' + value.substring(6, 8);
                default:
                    throw new Error('Invalid UPC-E value. The 7th character must be numeric');
            }
        } else {
            // unsupported type
            throw new Error('Unsupported format provided. Only UPC-A, UPC-E, and EAN-13 values are supported');
        }
    };
    /**
     * This function expects an 11-digit upc code sans the trailing check
     * digit. It will calculate and return the check digit for the value.
     * Source for calculation: https://en.wikipedia.org/wiki/Check_digit#UPC
     */
    private static calculateCheckDigit = (upcCode: string) => {
        const pattern = /^\d{11}$/;

        if (!pattern.test(upcCode)) {
            throw new Error('Input should contain exactly 11 digits');
        }

        const digits = Array.from(String(upcCode), Number);
        const oddSum = digits[0] + digits[2] + digits[4] + digits[6] + digits[8] + digits[10];
        const evenSum = digits[1] + digits[3] + digits[5] + digits[7] + digits[9];
        const tempSum = oddSum * 3 + evenSum;
        const modVal = tempSum % 10;
        return modVal > 0 ? 10 - modVal : 0;
    };
}

const type2EANPattern = /^02([0-9]{11})$/;
const type2UPCPattern = /^2([0-9]{11})$/;
const type4UPCPattern = /^4([0-9]{11})$/;
// Bizerba prints price-embedded barcodes with system digit 4 (type 4)
// Thus, we should read type 4 barcodes as if they were type 2.
// w.amazon.com/bin/view/POS_Devices_Team/Projects/VariableWeightPriceLimit/VariableWeightPriceLimitDesignDocument

export class BarcodeUtils {
    public static extractPLUFromType2Barcode = (itemId: string) => {
        if (BarcodeUtils.isEANType2Barcode(itemId)) {
            return itemId.substring(2, 7);
        }
        if (BarcodeUtils.isUPCType2Barcode(itemId) || BarcodeUtils.isUPCType4Barcode(itemId)) {
            return itemId.substring(1, 6);
        }
        return itemId;
    };

    public static isEANType2Barcode = (barcode: string) => {
        return type2EANPattern.test(barcode);
    };

    public static isUPCType2Barcode = (barcode: string) => {
        return type2UPCPattern.test(barcode);
    };

    public static isUPCType4Barcode = (barcode: string) => {
        return type4UPCPattern.test(barcode);
    };

    public static extractGtinFromGs1Barcode = (itemId: string) => {
        const parsedCode = GS1CodeParser.parse(itemId);
        const gtin = parsedCode.getValueForApplicationIdentifier(AiCode.GTIN);
        if (gtin) {
            return gtin;
        }

        return itemId;
    };
}
